import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, Inject, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, retryWhen, takeUntil, tap } from 'rxjs';

import { CORE_CONFIG } from '@personal-area/core';
import { Account, Leverages, LeverageSettings } from '@personal-area/global/accounts/models';
import { AccountService } from '@personal-area/global/accounts/services';
import { UnsubscribeController } from '@personal-area/global/common'
import { retryStrategy } from '@personal-area/global/http/services';

export interface LeverageModalData {
  currentLeverage: number;
  leverages: Leverages;
  leverageSetting: LeverageSettings;
  account: Account;
  language: string;
}

export interface Leverage {
  key: number,
  value: {
    text: string,
    error?: string
  },
}

const MEDIUM_RISK_THRESHOLD = 50;
const HIGH_RISK_THRESHOLD = 500;

@Component({
    selector: 'global-leverage-popup',
    templateUrl: './leverage-popup.component.html',
    styleUrls: ['./leverage-popup.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class LeveragePopupComponent extends UnsubscribeController {
  public readonly helpCenterDomain = inject(CORE_CONFIG).helpCenterDomain;
  public isPending$ = new BehaviorSubject(false);
  public checkedLeverage?: number;
  public lowRiskLeverages: Leverage[] = [];
  public mediumRiskLeverages: Leverage[] = [];
  public highRiskLeverages: Leverage[] = [];
  public isEquityWarningVisible: boolean;
  public isRequestFailed = false;
  public errorMessage?: string;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: LeverageModalData,
    private _accountService: AccountService,
    private _dialogRef: MatDialogRef<LeveragePopupComponent>,
  ) {
    super();

    Object
      .keys(this.data.leverages)
      .sort((a, b) => +a - +b)
      .forEach(key => {
        const keyValue = +key;
        const item = { key: keyValue, value: this.data.leverages[keyValue] };

        switch (true) {
          case (keyValue < MEDIUM_RISK_THRESHOLD):
            this.lowRiskLeverages.push(item);
            break;
          case (keyValue < HIGH_RISK_THRESHOLD):
            this.mediumRiskLeverages.push(item);
            break;
          default:
            this.highRiskLeverages.push(item);
            break;
        }
      });

    this.isEquityWarningVisible = !![...this.lowRiskLeverages, ...this.mediumRiskLeverages, ...this.highRiskLeverages]
      .find((leverage: Leverage) => !!leverage.value.text);
  }

  public submit(): void {
    this.isPending$.next(true);
    this.isRequestFailed = false;

    if (this.checkedLeverage) {
      this._accountService.updateLeverageSettings(this.data.account.id, this.checkedLeverage)
        .pipe(
          retryWhen(retryStrategy()),
          tap(() => {
            this.isPending$.next(false);
          }),
          takeUntil(this._unsubscribe),
        )
        .subscribe({
            next: () => this._dialogRef.close(this.checkedLeverage),
            error: (err: HttpErrorResponse) => {
              this.isRequestFailed = true;
              const ex = err.error.exception || err.error.mapException;
              if (ex && ex.hasOwnProperty('error')) {
                this.errorMessage = ex.error.tMessage || ex.error.message;
              }
            },
          },
        );
    }
  }
}
